/**
 * Participant-facing error states (§53): every outcome gets a title, an explanation and a
 * concrete next step. Messages come from ERROR_DEFINITIONS (single wording source); technical
 * failures additionally show the „Fehler-ID“ (§66). Never shows internals.
 */
import {
  ERROR_DEFINITIONS,
  ErrorCode,
  PUBLIC_REGISTRATION_STATE_LABELS,
  PublicRegistrationState,
} from '@lsc/shared';
import { isApiError } from '../api/errors';

export const NextStep = {
  /** Link to the code entry (`/`). */
  ENTER_CODE: 'ENTER_CODE',
  /** Retry the failed request. */
  RETRY: 'RETRY',
  /** Link to the registration status (`/status`). */
  SHOW_STATUS: 'SHOW_STATUS',
  /** Nothing to click – the hint text is the next step. */
  NONE: 'NONE',
} as const;
export type NextStep = (typeof NextStep)[keyof typeof NextStep];

export interface ErrorView {
  title: string;
  message: string;
  /** What the participant should do now. */
  hint: string | null;
  nextStep: NextStep;
  /** Show „Fehler-ID“ (technical errors only, §66). */
  showRequestId: boolean;
}

const CONTACT_HINT =
  'Ihr Ansprechpartner in Ihrem Unternehmen hilft Ihnen gerne weiter und kann Ihnen bei Bedarf einen neuen Code geben.';

const technicalError: ErrorView = {
  title: 'Technischer Fehler',
  message: ERROR_DEFINITIONS.INTERNAL_ERROR.message,
  hint: 'Falls der Fehler bestehen bleibt, nennen Sie Ihrem Ansprechpartner bitte die Fehler-ID.',
  nextStep: NextStep.RETRY,
  showRequestId: true,
};

const VIEWS: Partial<Record<ErrorCode, ErrorView>> = {
  [ErrorCode.ACTIVATION_CODE_INVALID]: {
    title: 'Code ungültig',
    message: ERROR_DEFINITIONS.ACTIVATION_CODE_INVALID.message,
    hint: 'Der Code steht in Ihrem Schreiben und hat das Format XXXXX-XXXXX.',
    nextStep: NextStep.ENTER_CODE,
    showRequestId: false,
  },
  [ErrorCode.ACTIVATION_CODE_USED]: {
    title: 'Code bereits verwendet',
    message: ERROR_DEFINITIONS.ACTIVATION_CODE_USED.message,
    hint: `Haben Sie Ihren Zugang bereits aktiviert? Dann finden Sie die Zugangsdaten in Ihrem E-Mail-Postfach. ${CONTACT_HINT}`,
    nextStep: NextStep.NONE,
    showRequestId: false,
  },
  [ErrorCode.ACTIVATION_CODE_DISABLED]: {
    title: 'Code deaktiviert',
    message: ERROR_DEFINITIONS.ACTIVATION_CODE_DISABLED.message,
    hint: CONTACT_HINT,
    nextStep: NextStep.NONE,
    showRequestId: false,
  },
  [ErrorCode.BATCH_DISABLED]: {
    title: 'Code deaktiviert',
    message: ERROR_DEFINITIONS.BATCH_DISABLED.message,
    hint: CONTACT_HINT,
    nextStep: NextStep.NONE,
    showRequestId: false,
  },
  [ErrorCode.ACTIVATION_CODE_EXPIRED]: {
    title: 'Code abgelaufen',
    message: ERROR_DEFINITIONS.ACTIVATION_CODE_EXPIRED.message,
    hint: CONTACT_HINT,
    nextStep: NextStep.NONE,
    showRequestId: false,
  },
  [ErrorCode.ACTIVATION_ALREADY_PENDING]: {
    title: 'Aktivierung läuft bereits',
    message: ERROR_DEFINITIONS.ACTIVATION_ALREADY_PENDING.message,
    hint: 'Klicken Sie auf den Bestätigungslink in der E-Mail, die wir Ihnen gesendet haben. Wird der Link nicht innerhalb von 24 Stunden bestätigt, kann der Code erneut verwendet werden.',
    nextStep: NextStep.NONE,
    showRequestId: false,
  },
  [ErrorCode.VERIFICATION_TOKEN_EXPIRED]: {
    title: 'E-Mail-Link abgelaufen',
    message: ERROR_DEFINITIONS.VERIFICATION_TOKEN_EXPIRED.message,
    hint: 'Geben Sie den Aktivierungscode aus Ihrem Schreiben erneut ein oder scannen Sie den QR-Code.',
    nextStep: NextStep.ENTER_CODE,
    showRequestId: false,
  },
  [ErrorCode.VERIFICATION_TOKEN_USED]: {
    title: 'E-Mail-Link bereits verwendet',
    message: ERROR_DEFINITIONS.VERIFICATION_TOKEN_USED.message,
    hint: 'Ihre E-Mail-Adresse ist bereits bestätigt. Den aktuellen Stand Ihrer Aktivierung sehen Sie in der Statusansicht.',
    nextStep: NextStep.SHOW_STATUS,
    showRequestId: false,
  },
  [ErrorCode.VERIFICATION_TOKEN_INVALID]: {
    title: 'E-Mail-Link ungültig',
    message: ERROR_DEFINITIONS.VERIFICATION_TOKEN_INVALID.message,
    hint: 'Haben Sie Ihre E-Mail-Adresse korrigiert oder eine neue Bestätigungsmail angefordert? Dann verwenden Sie bitte den Link aus der neuesten E-Mail.',
    nextStep: NextStep.NONE,
    showRequestId: false,
  },
  [ErrorCode.REGISTRATION_SESSION_INVALID]: {
    title: 'Sitzung abgelaufen',
    message: ERROR_DEFINITIONS.REGISTRATION_SESSION_INVALID.message,
    hint: 'Wenn Sie Ihre E-Mail-Adresse bereits bestätigt haben, erhalten Sie Ihre Zugangsdaten per E-Mail.',
    nextStep: NextStep.ENTER_CODE,
    showRequestId: false,
  },
  [ErrorCode.RATE_LIMITED]: {
    title: 'Zu viele Versuche',
    message: ERROR_DEFINITIONS.RATE_LIMITED.message,
    hint: null,
    nextStep: NextStep.RETRY,
    showRequestId: false,
  },
  [ErrorCode.CSRF_INVALID]: {
    title: 'Seite veraltet',
    message: ERROR_DEFINITIONS.CSRF_INVALID.message,
    hint: null,
    nextStep: NextStep.RETRY,
    showRequestId: true,
  },
};

/** Error view for any thrown value; unknown errors and 5xx map to „Technischer Fehler“. */
export function errorViewFor(error: unknown): ErrorView {
  if (!isApiError(error)) return technicalError;
  if (error.code === 'NETWORK_ERROR') {
    return {
      title: 'Keine Verbindung',
      message: error.message,
      hint: null,
      nextStep: NextStep.RETRY,
      showRequestId: false,
    };
  }
  if (
    error.code === 'INVALID_RESPONSE' ||
    error.code === ErrorCode.INTERNAL_ERROR ||
    error.status >= 500
  ) {
    return technicalError;
  }
  return (
    VIEWS[error.code] ?? {
      // Other expected business errors (Turnstile, resend limits, …): the server message says it all.
      title: 'Aktion nicht möglich',
      message: error.message,
      hint: null,
      nextStep: NextStep.RETRY,
      showRequestId: false,
    }
  );
}

/** Terminal registration states that are shown like errors on the status page. */
export const REGISTRATION_STATE_VIEWS = {
  [PublicRegistrationState.EXPIRED]: {
    title: 'Aktivierung abgelaufen',
    message: `${PUBLIC_REGISTRATION_STATE_LABELS.EXPIRED} Ihre E-Mail-Adresse wurde nicht innerhalb von 24 Stunden bestätigt.`,
    hint: 'Sie können die Aktivierung mit dem Code aus Ihrem Schreiben erneut starten.',
    nextStep: NextStep.ENTER_CODE,
    showRequestId: false,
  },
  [PublicRegistrationState.CANCELLED]: {
    title: 'Aktivierung abgebrochen',
    message: PUBLIC_REGISTRATION_STATE_LABELS.CANCELLED,
    hint: CONTACT_HINT,
    nextStep: NextStep.NONE,
    showRequestId: false,
  },
} as const satisfies Record<'EXPIRED' | 'CANCELLED', ErrorView>;
